/**
 * This section is the review-home body for one list dimension (matéria,
 * palavra-chave, baralho). It composes `components/Revisao/filtros/` and does
 * not import `components/filters/` (openspec change flashcards-revisao-redesign,
 * design.md Decisão 2).
 */

import { ReactNode, useMemo, useState } from "react";
import { Modal } from "antd";
import RevisaoFilterChip from "./filtros/RevisaoFilterChip";
import RevisaoFilterChipGroup from "./filtros/RevisaoFilterChipGroup";
import RevisaoFilterOverflow from "./filtros/RevisaoFilterOverflow";
import RevisaoFilterSearch from "./filtros/RevisaoFilterSearch";
import RevisaoFilterSection from "./filtros/RevisaoFilterSection";
import { revisaoMatchesQuery, revisaoOrderBySelection } from "./filtros/optionUtils";
import {
  REVISAO_FILTER_FOCUS_RING,
  REVISAO_VISIBLE_OPTIONS,
  type RevisaoSectionAccent,
  revisaoFilterCx,
  revisaoOverflowMode,
} from "./filtros/tokens";

export interface DimensaoOpcao {
  /** Stored selection. Subject id, keyword text, or deck name. */
  value: string;
  /** What the chip shows. */
  label: string;
}

export interface DimensaoSecaoProps {
  /** DOM id of the section. Chip group is labelled by `${id}-title` (RevisaoFilterSection already sets that id on the h3). */
  id: string;
  step: number;
  accent: RevisaoSectionAccent;
  icon: ReactNode;
  title: string;
  description: string;
  options: DimensaoOpcao[];
  selected: string[];
  onToggle: (value: string) => void;
  /** Screen-reader noun, plural, e.g. "matérias". */
  noun: string;
  searchPlaceholder: string;
  /** Shown when `options` is empty. No search, no overflow. */
  emptyLabel: string;
  /**
   * Set while a fixed day overrides this dimension (Matéria, Palavras-chave).
   * Every chip in the section renders disabled and the motivo is shown above
   * the options, tied to each chip by `aria-describedby`. No option is
   * removed — the rail count must not change when a day is fixed.
   */
  desabilitada?: { motivo: string };
  /**
   * The fixed day's own values (Baralhos). Those options render selected and
   * disabled; because the day, not the manual pick, decides the queue, no
   * other option in the section is actionable either — even without
   * `desabilitada` set.
   */
  fixadas?: string[];
}

/**
 * Chosen values stay in the visible window. `revisaoOrderBySelection` orders
 * strings, so this maps back onto the option whose chip label differs from
 * the stored value.
 */
function orderOptionsBySelection(options: DimensaoOpcao[], selected: string[]): DimensaoOpcao[] {
  const byValue = new Map(options.map((option) => [option.value, option]));
  return revisaoOrderBySelection(
    options.map((option) => option.value),
    selected,
  ).flatMap((value) => {
    const option = byValue.get(value);
    return option ? [option] : [];
  });
}

function NoMatches({ onClear }: { onClear: () => void }) {
  return (
    <div className="rounded-chip border border-dashed border-edge px-4 py-6 text-center">
      <p className="text-[13px] font-medium text-ink">Nenhum filtro encontrado</p>
      <p className="mt-1 text-xs text-ink-muted">Tente outro termo de busca.</p>
      <button
        type="button"
        onClick={onClear}
        className={revisaoFilterCx(
          "mt-2 rounded-chip px-1 text-xs font-medium text-brand hover:underline",
          REVISAO_FILTER_FOCUS_RING,
        )}
      >
        Limpar busca
      </button>
    </div>
  );
}

/**
 * Full list for one dimension. Lives here because value and label differ
 * (subject id vs name, deck name vs "Name · count"); RevisaoFilterOptionsDialog
 * only accepts `string[]` and prints the string as the chip label.
 */
function DimensaoOpcoesDialog({
  title,
  noun,
  options,
  selected,
  onToggle,
  onClose,
  searchPlaceholder,
  disabled,
  describedBy,
}: {
  title: string;
  noun: string;
  options: DimensaoOpcao[];
  selected: string[];
  onToggle: (value: string) => void;
  onClose: () => void;
  searchPlaceholder: string;
  /** Mirrors the section's own disabled state — the "Ver todas" list is the same options, not a loophole around it. */
  disabled?: boolean;
  describedBy?: string;
}) {
  const [query, setQuery] = useState("");

  const visible = useMemo(() => {
    const matching = options.filter((option) => revisaoMatchesQuery(option.label, query));
    return orderOptionsBySelection(matching, selected);
  }, [options, query, selected]);

  return (
    <Modal
      open
      onCancel={onClose}
      footer={null}
      title={null}
      width="min(760px, 94vw)"
      destroyOnClose
      className="acertei-filter-modal"
      aria-label={title}
    >
      <div className="flex max-h-[80dvh] flex-col bg-surface sm:max-h-[70vh]">
        <div className="border-b border-edge px-4 py-4 sm:px-5">
          <h2 className="text-base font-semibold text-ink">{title}</h2>
          <p className="mt-0.5 text-[13px] text-ink-muted">
            {`${options.length} ${noun} · ${selected.length} selecionadas`}
          </p>
          <RevisaoFilterSearch
            className="mt-3"
            variant="section"
            value={query}
            onChange={setQuery}
            placeholder={searchPlaceholder}
            ariaLabel={`Buscar em ${title}`}
          />
        </div>

        <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto px-4 py-4 sm:px-5">
          {visible.length === 0 ? (
            <div className="py-10 text-center">
              <p className="text-sm font-medium text-ink">Nenhum filtro encontrado</p>
              <p className="mt-1 text-[13px] text-ink-muted">Tente outro termo de busca.</p>
            </div>
          ) : (
            <RevisaoFilterChipGroup ariaLabel={title}>
              {visible.map((option) => (
                <RevisaoFilterChip
                  key={option.value}
                  label={option.label}
                  selected={selected.includes(option.value)}
                  disabled={disabled}
                  describedBy={describedBy}
                  onToggle={() => onToggle(option.value)}
                />
              ))}
            </RevisaoFilterChipGroup>
          )}
        </div>

        <div className="border-t border-edge px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] text-right sm:px-5">
          <button
            type="button"
            onClick={onClose}
            className="h-10 w-full rounded-input bg-brand px-5 text-sm font-semibold text-brand-contrast transition-[filter] hover:brightness-110 sm:w-auto"
          >
            Concluir
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default function DimensaoSecao({
  id,
  step,
  accent,
  icon,
  title,
  description,
  options,
  selected,
  onToggle,
  noun,
  searchPlaceholder,
  emptyLabel,
  desabilitada,
  fixadas,
}: DimensaoSecaoProps): JSX.Element {
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const hasOptions = options.length > 0;
  // A fixed day overrides the draft selection for display (design, Decisão 5)
  // and, on its own, makes the whole section non-actionable — not just the
  // values it names.
  const sectionDisabled = Boolean(desabilitada) || Boolean(fixadas && fixadas.length > 0);
  const motivoId = desabilitada ? `${id}-motivo` : undefined;
  const effectiveSelected = fixadas ?? selected;

  const ordered = useMemo(() => {
    const matching = options.filter((option) => revisaoMatchesQuery(option.label, query));
    return orderOptionsBySelection(matching, effectiveSelected);
  }, [options, query, effectiveSelected]);

  const mode = revisaoOverflowMode(ordered.length);
  const windowed =
    mode === "inline" && !expanded ? ordered.slice(0, REVISAO_VISIBLE_OPTIONS) : ordered;
  const shown = mode === "modal" ? windowed.slice(0, REVISAO_VISIBLE_OPTIONS) : windowed;
  const showOverflow = ordered.length > 0 && mode !== "none";

  return (
    <>
      <RevisaoFilterSection
        id={id}
        step={step}
        icon={icon}
        accent={accent}
        title={title}
        description={description}
        search={
          hasOptions ? (
            <RevisaoFilterSearch
              variant="section"
              value={query}
              onChange={setQuery}
              placeholder={searchPlaceholder}
              ariaLabel={`Buscar em ${title}`}
            />
          ) : undefined
        }
        headerRight={
          showOverflow ? (
            <RevisaoFilterOverflow
              mode={mode}
              total={ordered.length}
              expanded={expanded}
              onToggleExpand={() => setExpanded((current) => !current)}
              onViewAll={() => setDialogOpen(true)}
              noun={noun}
            />
          ) : undefined
        }
      >
        {desabilitada ? (
          <p id={motivoId} className="mb-3 text-[13px] text-ink-muted">
            {desabilitada.motivo}
          </p>
        ) : null}
        {!hasOptions ? (
          <p className="text-sm text-ink-muted">{emptyLabel}</p>
        ) : ordered.length === 0 ? (
          <NoMatches onClear={() => setQuery("")} />
        ) : (
          <RevisaoFilterChipGroup labelledBy={`${id}-title`} className="flex flex-wrap gap-2">
            {shown.map((option) => (
              <RevisaoFilterChip
                key={option.value}
                label={option.label}
                selected={effectiveSelected.includes(option.value)}
                disabled={sectionDisabled}
                describedBy={motivoId}
                onToggle={() => onToggle(option.value)}
              />
            ))}
          </RevisaoFilterChipGroup>
        )}
      </RevisaoFilterSection>

      {dialogOpen ? (
        <DimensaoOpcoesDialog
          title={title}
          noun={noun}
          options={options}
          selected={effectiveSelected}
          onToggle={onToggle}
          onClose={() => setDialogOpen(false)}
          searchPlaceholder={searchPlaceholder}
          disabled={sectionDisabled}
          describedBy={motivoId}
        />
      ) : null}
    </>
  );
}
